// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Truncate from "@bg/App/Components/Truncate";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Clickable from "@insite/mobius/Clickable";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { DetailedReactHTMLElement } from "react";
import { css } from "styled-components";

type Props = {
    extendedStyles?: XNewsItemStyles;
    imgSrc: string;
    date: Date;
    title: string;
    readMoreLink: string | undefined;
    description: string | DetailedReactHTMLElement<{}, HTMLElement> | DetailedReactHTMLElement<{}, HTMLElement>[];
    lines?: number;
};

interface XNewsItemStyles {
    wrapper?: InjectableCss;
    overlay?: InjectableCss;
    date?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    description?: InjectableCss;
    readMore?: InjectableCss;
    readMoreText?: TypographyPresentationProps;
}

export const xNewsItemStyles: XNewsItemStyles = {
    wrapper: {
        css: css`
            position: relative;
            height: 100%;
            &:hover,
            &:active,
            &:focus {
                & .overlay {
                    height: 100%;
                }
                & .title {
                    margin-top: 89px;
                }
                & .description {
                    display: block;
                    margin-top: 18px;
                    height: 60px;
                    overflow: hidden;
                }
                & .readMore {
                    display: flex;
                    position: absolute;
                    bottom: 16px;
                }
            }
        `,
    },
    overlay: {
        css: css`
            position: absolute;
            bottom: 0px;
            left: 0;
            right: 0;
            overflow: hidden;
            width: 100%;
            height: 105px;
            width: 100%;
            opacity: 0.85;
            background-color: ${getColor("text")};
            transition: 0.5s ease;
            padding: 20px;
            text-align: left;
        `,
    },
    date: {
        size: 12,
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.xSmall,
        color: "common.white",
        css: css`
            letter-spacing: 1px;
        `,
    },
    title: {
        size: 18,
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.small,
        color: "common.white",
        css: css`
            margin-top: 10px;
        `,
    },
    description: {
        css: css`
            display: none;
            max-width: 279px;
            & span {
                font-size: ${fontConfig.fontSize.regular}px;
                font-weight: ${fontConfig.weight.light};
                letter-spacing: 0;
                line-height: 24px;
                color: ${getColor("common.white")};
            }
        `,
    },
    readMore: {
        css: css`
            display: none;
            align-items: center;
            & span {
                color: ${getColor("common.white")};
                margin-left: 5px;
            }
            & svg {
                color: ${getColor("common.white")};
                width: 20px;
                height: 20px;
            }
        `,
    },
    readMoreText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "17px",
        css: css`
            letter-spacing: 1px;
            color: ${getColor("common.white")};
            text-transform: uppercase;
        `,
    },
};

const XNewsItem = ({ extendedStyles, imgSrc, date, title, description, readMoreLink, lines }: Props) => {
    const [styles] = React.useState(() => mergeToNew(xNewsItemStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper}>
            <img alt={title} src={imgSrc} height="475px" />
            <StyledWrapper {...styles.overlay} className="overlay">
                <Typography as="p" {...styles.date} className="date">
                    <LocalizedDateTime
                        dateTime={date}
                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                    />
                </Typography>
                <Typography as="h3" {...styles.title} className="title">
                    <Truncate lines={2} lineHeight={24}>
                        {title}
                    </Truncate>
                </Typography>
                <StyledWrapper {...styles.description} className="description">
                    <Truncate lines={lines || 7} lineHeight={24}>
                        {description}
                    </Truncate>
                </StyledWrapper>
                <Clickable {...styles.readMore} className="readMore" href={readMoreLink}>
                    <Typography as="p" {...styles.readMoreText}>
                        {translate("READ MORE")}
                    </Typography>
                    <ChevronRight />
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default XNewsItem;
